import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    generateHomePicture as generateHomePictureAPI,
    getHomePictureTask,
    listHomePictures as listHomePicturesAPI,
    deleteHomePictures as deleteHomePicturesAPI,
    HomePictureTask,
    HomePicture,
} from "@/Data/World";
import ss from '@/Data/Session';

type HomePictureState = {
    // Home pictures by addressid (array of all pictures)
    homePicturesByUser: Record<string, HomePicture[]>;

    // Latest home picture image by addressid (for quick access)
    homePictures: Record<string, string>;

    // Active task tracking
    currentTaskId: string | null;
    currentAddressId: string | null;
    task: HomePictureTask | null;

    // Loading state
    loading: boolean;

    // Error state
    error: string | null;
};

const STORAGE_KEY = 'world:homePicture';

const loadFromStorage = (): Partial<HomePictureState> => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to load HomePicture from storage:', e);
    }
    return {};
};

const saveToStorage = (state: HomePictureState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            homePictures: state.homePictures,
        }));
    } catch (e) {
        console.error('Failed to save HomePicture to storage:', e);
    }
};

const defaultState: HomePictureState = {
    homePicturesByUser: {},
    homePictures: {},
    currentTaskId: null,
    currentAddressId: null,
    task: null,
    loading: false,
    error: null,
};

const homePictureState = atom<HomePictureState>({
    key: 'homePictureState',
    default: { ...defaultState, ...loadFromStorage() },
});

/**
 * Hook for managing home picture generation
 */
export const useHomePicture = () => {
    const [state, setState] = useRecoilState(homePictureState);

    const updateState = useCallback((newState: Partial<HomePictureState>) => {
        setState(prev => {
            const updated = { ...prev, ...newState };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    const {
        homePicturesByUser,
        homePictures,
        currentTaskId,
        currentAddressId,
        task,
        loading,
        error,
    } = state;

    /**
     * Fetch home pictures for a user from API
     */
    const fetchHomePictures = useCallback(async (addressid: string): Promise<HomePicture[]> => {
        const pictures = await listHomePicturesAPI(addressid);

        if (pictures.length > 0) {
            setState(prev => {
                const updated = {
                    ...prev,
                    homePicturesByUser: {
                        ...prev.homePicturesByUser,
                        [addressid]: pictures,
                    },
                    homePictures: {
                        ...prev.homePictures,
                        [addressid]: pictures[0].image, // Latest picture
                    },
                };
                saveToStorage(updated);
                return updated;
            });
        }

        return pictures;
    }, [setState]);

    /**
     * Start home picture generation (non-blocking)
     * Returns taskId immediately, stores in state for polling
     */
    const startHomePictureGeneration = useCallback(async (params: {
        room_image: string;
        addressid?: string;
        prompt?: string;
    }): Promise<string | null> => {
        updateState({
            loading: true,
            error: null,
            task: null,
            currentTaskId: null,
            currentAddressId: params.addressid || null,
        });

        const taskid = await generateHomePictureAPI(params);

        if (!taskid) {
            updateState({
                loading: false,
                error: 'Failed to start home picture generation',
            });
            return null;
        }

        updateState({ currentTaskId: taskid });
        return taskid;
    }, [updateState]);

    /**
     * Check home picture task status once (for interval-based polling)
     * Uses stored taskId and addressId from state if not provided
     */
    const checkHomePictureTask = useCallback(async (
        taskid?: string,
        addressid?: string
    ): Promise<HomePictureTask | null> => {
        const effectiveTaskId = taskid || currentTaskId;
        const effectiveAddressId = addressid || currentAddressId;

        if (!effectiveTaskId) {
            return null;
        }

        const taskResult = await getHomePictureTask(effectiveTaskId);

        if (!taskResult) {
            updateState({
                loading: false,
                error: 'Failed to get task status',
                currentTaskId: null,
                currentAddressId: null,
            });
            return null;
        }

        updateState({ task: taskResult });

        if (taskResult.status === 'completed' && taskResult.image) {
            const key = effectiveAddressId || taskResult.userid;

            setState(prev => {
                const updated = {
                    ...prev,
                    homePictures: {
                        ...prev.homePictures,
                        [key]: taskResult.image!,
                    },
                    loading: false,
                    task: null,
                    currentTaskId: null,
                    currentAddressId: null,
                };
                saveToStorage(updated);
                return updated;
            });

            return taskResult;
        }

        if (taskResult.status === 'failed') {
            updateState({
                loading: false,
                error: 'Home picture generation failed',
                task: null,
                currentTaskId: null,
                currentAddressId: null,
            });
            return taskResult;
        }

        // Still processing
        return taskResult;
    }, [setState, updateState, currentTaskId, currentAddressId]);

    /**
     * Cancel/reset home picture generation state
     */
    const cancelHomePictureGeneration = useCallback(() => {
        updateState({
            loading: false,
            task: null,
            currentTaskId: null,
            currentAddressId: null,
            error: null,
        });
    }, [updateState]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        updateState({ error: null });
    }, [updateState]);

    /**
     * Get cached home picture for addressid
     */
    const getHomePicture = useCallback((addressid: string): string | null => {
        return homePictures[addressid] || null;
    }, [homePictures]);

    /**
     * Delete home pictures by image URLs
     * @param images Array of image URLs to delete
     * @param addressid User's addressid to update local state
     * @returns Number of deleted pictures
     */
    const deleteHomePictures = useCallback(async (images: string[], givenAddressid?: string): Promise<number> => {
        const deleted = await deleteHomePicturesAPI(images);

        const addressid = givenAddressid || (ss.getCurrentSession().addressid as string | undefined);

        if (deleted > 0 && addressid) {
            setState(prev => {
                const currentPictures = prev.homePicturesByUser[addressid] || [];
                const filteredPictures = currentPictures.filter(p => !images.includes(p.image));

                const updated = {
                    ...prev,
                    homePicturesByUser: {
                        ...prev.homePicturesByUser,
                        [addressid]: filteredPictures,
                    },
                    homePictures: {
                        ...prev.homePictures,
                        [addressid]: filteredPictures[0]?.image || '',
                    },
                };
                saveToStorage(updated);
                return updated;
            });
        }

        return deleted;
    }, [setState]);

    /**
     * Check if home picture generation is in progress
     */
    const isGenerating = loading && currentTaskId !== null;

    return {
        // State
        homePicturesByUser,
        homePictures,
        currentTaskId,
        task,
        loading,
        isGenerating,
        error,

        // Actions
        fetchHomePictures,
        startHomePictureGeneration,
        checkHomePictureTask,
        cancelHomePictureGeneration,
        getHomePicture,
        deleteHomePictures,
        clearError,
    };
};
